import type { VoidFunctionComponent } from 'react';

import { dependencyInfoOptionsWithDescriptions } from '../../../../shared/reports';
import LabeledCheckmark from '../../generic/LabeledCheckmark';
import { useReportFlow } from '../hooks/useReportFlow';

import styles from './ReportOptions.module.less';

const standardOptions = [
  {
    id: 'dependencies-summary',
    label: 'Dependencies Summary',
    tooltip: 'A summary of all dependencies found in the project.',
  },
  {
    id: 'direct-dependencies',
    label: 'Direct Dependencies',
    tooltip: 'Dependencies that are directly referenced by your project.',
  },
  {
    id: 'concluded-licenses',
    label: 'Concluded License(s)',
    tooltip: dependencyInfoOptionsWithDescriptions.ConcludedLicense,
  },
  {
    id: 'copyright',
    label: 'Copyright',
    tooltip: dependencyInfoOptionsWithDescriptions.Copyrights,
  },
  {
    id: 'notice-files',
    label: 'Notice File(s)',
    tooltip: dependencyInfoOptionsWithDescriptions.NoticeFiles,
  },
];

/**
 * Static options which are always included by FOSSA.
 *
 * Note that we need this wrapper div, since the parent is a flex container
 * which provides proper spacing between child elements.
 */
const StandardOptions: VoidFunctionComponent = () => (
  <div>
    <h4 className={styles.heading}>Included by FOSSA</h4>
    <div className={styles.list}>
      {standardOptions.map((option) => (
        <LabeledCheckmark
          key={option.id}
          id={option.id}
          label={option.label}
          tooltip={option.tooltip}
          tooltipPlacement="left"
        />
      ))}
    </div>
  </div>
);

/**
 * Custom options which are user-defined.
 *
 * Note that we need this div at the top, since the parent is a flex container
 * which provides proper spacing between child elements.
 */
const CustomOptions: VoidFunctionComponent = () => (
  <div>
    Custom options coming soon!
  </div>
);

/**
 * The main component which renders the report options.
 * It is aware of the active report flow.
 */
export const ReportOptions: VoidFunctionComponent = () => {
  const [flow] = useReportFlow();
  return flow === 'custom' ? <CustomOptions /> : <StandardOptions />;
};
